import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Flex, Text, Button, Icon, HStack, Image, Grid, GridItem, Divider, } from "@chakra-ui/react";
import { keyframes } from "@chakra-ui/system";
import { useEffect, useState, useRef } from "react";
import { FiUsers } from "react-icons/fi";
import { IMG_URL } from "../common/Constant";
import { useNavigate } from "react-router-dom";
import ExperienceCard from "./ExperienceCard";
import SeatingOptionCard from "./SeatingOptionCard";
import PerksCard from "./PerksCard";
import VideoThumbnail from "./VideoThumbnail";
import { EffectCoverflow } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import MultiTag from "./MultiTag";
import { normalize, normalizeKey } from "../../shared-utils/utils";
const formatPrice = (price) => price.toLocaleString("en-IN");
const fadeInSlideUp = keyframes `
  0% { 
    opacity: 0; 
    transform: translateY(20px); 
  }
  100% { 
    opacity: 1; 
    transform: translateY(0); 
  }
`;
const calculatePrice = (coverPrice, entryPrice, discount, discountType) => {
    const total = coverPrice + entryPrice;
    if (discountType === "none")
        return total;
    const discountValue = discountType === "percentage"
        ? (total * discount) / 100
        : Math.min(discount, total);
    return total - discountValue;
};
export default function TableDesc({ areaName, data, areaInfo, areaPriceRange, filters, isNewTableFormat = false, femaleCount = 0, isRecommended = false, isSvgLayout = false, onProceed, }) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    const navigate = useNavigate();
    const [tablesForArea, setTablesForArea] = useState([]);
    const [selectedTable, setSelectedTable] = useState(null);
    const [isPerksCardLoading, setIsPerksCardLoading] = useState(false);
    const [shouldAnimate, setShouldAnimate] = useState(false);
    const previousTableIdRef = useRef(null);
    const swiperContainerRef = useRef(null);
    const isInsufficientLadies = (selectedTable === null || selectedTable === void 0 ? void 0 : selectedTable.minLadiesRequired) > 0 &&
        femaleCount < (selectedTable === null || selectedTable === void 0 ? void 0 : selectedTable.minLadiesRequired);
    const sectionAreas = (_a = areaInfo === null || areaInfo === void 0 ? void 0 : areaInfo.data) !== null && _a !== void 0 ? _a : [];
    // Parse areaName to extract just the area part for section matching
    const actualAreaName = areaName.includes("_")
        ? areaName.split("_")[0]
        : areaName;
    const selectedArea = sectionAreas === null || sectionAreas === void 0 ? void 0 : sectionAreas.find((item) => normalizeKey(item.areaName) === normalizeKey(actualAreaName));
    const areaDescription = (_b = selectedArea === null || selectedArea === void 0 ? void 0 : selectedArea.description) !== null && _b !== void 0 ? _b : 0;
    const areaMaxPeoplePerTable = (_c = selectedArea === null || selectedArea === void 0 ? void 0 : selectedArea.maxPeople) !== null && _c !== void 0 ? _c : 0;
    // Use actualAreaName for areaPriceRange lookup
    const safeAreaName = actualAreaName === null || actualAreaName === void 0 ? void 0 : actualAreaName.trim();
    const areaData = areaPriceRange === null || areaPriceRange === void 0 ? void 0 : areaPriceRange[safeAreaName];
    let priceRangeText = null;
    const min = areaData === null || areaData === void 0 ? void 0 : areaData.minPrice;
    const max = areaData === null || areaData === void 0 ? void 0 : areaData.maxPrice;
    const hasMin = min !== null && min !== undefined;
    const hasMax = max !== null && max !== undefined;
    if (hasMin && hasMax && min === 0 && max === 0) {
        priceRangeText = "FREE";
    }
    else if (min === 0 && hasMax && max > 0) {
        priceRangeText = `Up to ₹${formatPrice(max)}`;
    }
    else if (hasMin && hasMax) {
        if (min === max) {
            priceRangeText = min > 0 ? `₹${formatPrice(min)}` : null;
        }
        else {
            priceRangeText = `₹${formatPrice(min)} - ₹${formatPrice(max)}`;
        }
    }
    else if (hasMin && min > 0) {
        priceRangeText = `₹${formatPrice(min)}`;
    }
    else if (hasMax && max > 0) {
        priceRangeText = `Up to ₹${formatPrice(max)}`;
    }
    // Use actualAreaName for finding images in section areas
    const rawImages = ((_d = sectionAreas === null || sectionAreas === void 0 ? void 0 : sectionAreas.find((item) => item.areaName === actualAreaName)) === null || _d === void 0 ? void 0 : _d.images) || [];
    // Helper function to detect if an item is a video
    const isVideo = (url) => {
        const baseUrl = url.split("?")[0];
        return /(0?mp4|0?mov|0?webm|\.mp4|\.mov|\.webm)$/i.test(baseUrl);
    };
    // Sort images to show videos first
    const images = [...rawImages].sort((a, b) => {
        const aIsVideo = isVideo(a);
        const bIsVideo = isVideo(b);
        if (aIsVideo && !bIsVideo)
            return -1;
        if (!aIsVideo && bIsVideo)
            return 1;
        return 0;
    });
    useEffect(() => {
        if (!areaName || !Array.isArray(data)) {
            setTablesForArea([]);
            setSelectedTable(null);
            return;
        }
        // Parse areaName to support both formats:
        // New format: "SUNDECK_REGULAR" (areaName_tableType)
        // Old format: "SUNDECK" or "SUNDECK VIP" (just areaName)
        let targetAreaName = areaName;
        let targetTableType = null;
        if (areaName.includes("_")) {
            // New format: Split by underscore
            const parts = areaName.split("_");
            targetAreaName = parts[0];
            targetTableType = parts.slice(1).join("_"); // Handle cases like "SUPER_VIP"
        }
        const filteredTables = data === null || data === void 0 ? void 0 : data.filter((table) => {
            const areaMatches = normalize(table === null || table === void 0 ? void 0 : table.areaName) === normalize(targetAreaName);
            // If we have a tableType from the ID, filter by it as well
            if (targetTableType) {
                const tableTypeMatches = normalize(table === null || table === void 0 ? void 0 : table.tableType) === normalize(targetTableType);
                return areaMatches && tableTypeMatches;
            }
            // Old format: just match by areaName
            return areaMatches;
        });
        if (!(filteredTables === null || filteredTables === void 0 ? void 0 : filteredTables.length)) {
            setTablesForArea([]);
            setSelectedTable(null);
            return;
        }
        const getNumber = (value) => {
            const num = parseFloat(value);
            return isFinite(num) && !isNaN(num) ? num : 0;
        };
        const ultimateIndex = filteredTables.reduce((bestIndex, curr, idx, arr) => {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            const currTotal = calculatePrice(getNumber(curr === null || curr === void 0 ? void 0 : curr.coverPrice), getNumber(curr === null || curr === void 0 ? void 0 : curr.entryPrice), getNumber((_a = curr === null || curr === void 0 ? void 0 : curr.discountValue) !== null && _a !== void 0 ? _a : 0), (_b = curr === null || curr === void 0 ? void 0 : curr.discountType) !== null && _b !== void 0 ? _b : "");
            const bestTotal = calculatePrice(getNumber((_c = arr[bestIndex]) === null || _c === void 0 ? void 0 : _c.coverPrice), getNumber((_d = arr[bestIndex]) === null || _d === void 0 ? void 0 : _d.entryPrice), getNumber((_f = (_e = arr[bestIndex]) === null || _e === void 0 ? void 0 : _e.discountValue) !== null && _f !== void 0 ? _f : 0), (_h = (_g = arr[bestIndex]) === null || _g === void 0 ? void 0 : _g.discountType) !== null && _h !== void 0 ? _h : "");
            return currTotal > bestTotal ? idx : bestIndex;
        }, 0);
        let tablesWithFlag = filteredTables === null || filteredTables === void 0 ? void 0 : filteredTables.map((table, index) => {
            var _a, _b, _c, _d, _e, _f, _g;
            return ({
                ...table,
                coverPrice: (_a = table === null || table === void 0 ? void 0 : table.coverPrice) !== null && _a !== void 0 ? _a : 0,
                entryPrice: (_b = table === null || table === void 0 ? void 0 : table.entryPrice) !== null && _b !== void 0 ? _b : 0,
                price: calculatePrice((_c = table === null || table === void 0 ? void 0 : table.coverPrice) !== null && _c !== void 0 ? _c : 0, (_d = table === null || table === void 0 ? void 0 : table.entryPrice) !== null && _d !== void 0 ? _d : 0, (_e = table === null || table === void 0 ? void 0 : table.discountValue) !== null && _e !== void 0 ? _e : 0, (_f = table === null || table === void 0 ? void 0 : table.discountType) !== null && _f !== void 0 ? _f : ""),
                isUltimate: index === ultimateIndex && ((_g = table === null || table === void 0 ? void 0 : table.availableTables) !== null && _g !== void 0 ? _g : 0) > 0,
            });
        });
        if ((filters === null || filters === void 0 ? void 0 : filters.sortBy) === "price_high") {
            tablesWithFlag.sort((a, b) => {
                var _a, _b, _c, _d;
                const totalA = calculatePrice(a.coverPrice, a.entryPrice, (_a = a.discountValue) !== null && _a !== void 0 ? _a : 0, (_b = a.discountType) !== null && _b !== void 0 ? _b : "");
                const totalB = calculatePrice(b.coverPrice, b.entryPrice, (_c = b.discountValue) !== null && _c !== void 0 ? _c : 0, (_d = b.discountType) !== null && _d !== void 0 ? _d : "");
                return totalB - totalA;
            });
        }
        else if ((filters === null || filters === void 0 ? void 0 : filters.sortBy) === "price_low") {
            tablesWithFlag.sort((a, b) => {
                var _a, _b, _c, _d;
                const totalA = calculatePrice(a.coverPrice, a.entryPrice, (_a = a.discountValue) !== null && _a !== void 0 ? _a : 0, (_b = a.discountType) !== null && _b !== void 0 ? _b : "");
                const totalB = calculatePrice(b.coverPrice, b.entryPrice, (_c = b.discountValue) !== null && _c !== void 0 ? _c : 0, (_d = b.discountType) !== null && _d !== void 0 ? _d : "");
                return totalA - totalB;
            });
        }
        else {
            tablesWithFlag.sort((a, b) => {
                if (a.isUltimate && !b.isUltimate)
                    return 1;
                if (!a.isUltimate && b.isUltimate)
                    return -1;
                return a.price - b.price;
            });
        }
        setTablesForArea(tablesWithFlag);
        // Always set selectedTable when tables change
        if (tablesWithFlag.length > 0) {
            setSelectedTable(tablesWithFlag[0]);
        }
        else {
            setSelectedTable(null);
        }
    }, [areaName, data, filters === null || filters === void 0 ? void 0 : filters.sortBy]);
    // Ensure selectedTable is set when area changes (fallback for edge cases)
    useEffect(() => {
        if (tablesForArea.length > 0) {
            // Check if selectedTable exists in the current tablesForArea
            const currentTableExists = selectedTable &&
                tablesForArea.some((table) => (selectedTable.slotId && table.slotId === selectedTable.slotId) ||
                    (selectedTable.categoryId &&
                        table.categoryId === selectedTable.categoryId));
            // If selectedTable doesn't exist in current tables or is null, set to first table
            if (!currentTableExists) {
                setSelectedTable(tablesForArea[0]);
            }
        }
        else if (tablesForArea.length === 0) {
            setSelectedTable(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [areaName, tablesForArea.length]);
    useEffect(() => {
        if (!selectedTable) {
            setIsPerksCardLoading(false);
            setShouldAnimate(false);
            previousTableIdRef.current = null;
            return;
        }
        const currentTableId = selectedTable.slotId || selectedTable.categoryId;
        const isInitialLoad = previousTableIdRef.current === null;
        const isTableChanged = previousTableIdRef.current !== currentTableId;
        // If table changed (not initial load), show loading animation
        if (isTableChanged && !isInitialLoad) {
            setIsPerksCardLoading(true);
            setShouldAnimate(false);
            // Use requestAnimationFrame to ensure DOM is ready, then trigger animation
            const rafId = requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    setIsPerksCardLoading(false);
                    setShouldAnimate(true);
                });
            });
            previousTableIdRef.current = currentTableId;
            return () => cancelAnimationFrame(rafId);
        }
        // On initial load, show immediately with animation
        if (isInitialLoad) {
            setIsPerksCardLoading(false);
            previousTableIdRef.current = currentTableId;
            // Trigger animation after a brief delay to ensure DOM is ready
            const rafId = requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    setShouldAnimate(true);
                });
            });
            return () => cancelAnimationFrame(rafId);
        }
    }, [selectedTable]);
    const handleImageClick = (index) => {
        if (!images || images.length === 0)
            return;
        setTimeout(() => {
            navigate("/vibes", {
                state: {
                    data: {
                        vibes: {
                            vibes: images,
                            defaultIdx: index,
                        },
                    },
                },
            });
        }, 50);
    };
    return (_jsxs(Box, { overflowY: "auto", overflowX: "hidden", mx: "auto", py: 5, px: 2, h: "100%", pb: "110px", children: [_jsxs(Flex, { direction: "column", alignItems: "start", gap: 1, children: [_jsx(Flex, { align: "center", justify: "space-between", children: _jsxs(Flex, { align: "center", gap: 2, children: [_jsx(Image, { src: `${IMG_URL}/icons/location-pin.svg`, alt: "loc" }), _jsx(Text, { fontSize: "16px", fontWeight: "600", color: "white", children: areaName.replace(/_/g, " ") })] }) }), isRecommended && (_jsxs(Flex, { align: "center", gap: 2, children: [_jsx(Box, { w: "6px", h: "6px", bg: "red.500", borderRadius: "full" }), _jsx(Text, { fontSize: "12px", fontWeight: "600", color: "white", fontStyle: "italic", lineHeight: "24px", children: "Recommended for best experience" })] })), _jsxs(Flex, { gap: 2, wrap: "wrap", alignContent: "center", justifyContent: "space-between", w: "full", mt: 2, children: [_jsx(HStack, { children: _jsx(MultiTag, { type: "fastFilling" }) }), isSvgLayout && priceRangeText && (_jsx(Text, { fontSize: "12px", fontWeight: "400", color: "white", children: priceRangeText })), !isSvgLayout && areaMaxPeoplePerTable && (_jsxs(Flex, { align: "center", gap: 1, children: [_jsx(Icon, { as: FiUsers, boxSize: "20px", color: "#9D9D9D" }), _jsx(Text, { fontSize: "12px", fontWeight: "400", color: "white", children: areaMaxPeoplePerTable })] }))] })] }), _jsxs(Box, { mt: 5, children: [_jsx(Flex, { justify: "center", align: "center", w: "100%", children: _jsx(Grid, { templateColumns: "repeat(3, 111px)", templateRows: `repeat(${Math.ceil(Math.min(images.length, 3) / 3)}, 111px)`, gap: 1, borderRadius: "12px", overflow: "hidden", w: "fit-content", children: images.slice(0, 3).map((item, index) => {
                                return (_jsx(GridItem, { position: "relative", overflow: "hidden", cursor: "pointer", onClick: () => handleImageClick(index), children: _jsxs(Box, { position: "relative", w: "111px", h: "111px", children: [_jsx(VideoThumbnail, { src: item, onClick: () => handleImageClick(index), autoPlay: isVideo(item) }), index === 2 && images.length > 3 && (_jsx(Flex, { position: "absolute", inset: 0, align: "center", justify: "center", bg: "rgba(0,0,0,0.4)", children: _jsxs(Text, { fontSize: "lg", fontWeight: "700", color: "white", children: ["+", images.length - 3] }) }))] }) }, index));
                            }) }) }), areaDescription ? (_jsx(Text, { fontSize: "12px", fontWeight: "400", color: "#9D9D9D", mt: 4, children: areaDescription !== null && areaDescription !== void 0 ? areaDescription : "" })) : null] }), _jsxs(Box, { mt: 5, pb: 4, children: [_jsx(Divider, { borderColor: "#302F2F", my: 4 }), _jsx(Text, { fontSize: "16px", fontWeight: "600", color: "white", mb: 3, children: "Book Online & Save" }), _jsxs(Flex, { align: "center", justify: "space-between", mb: 4, children: [_jsx(Text, { fontSize: "12px", fontWeight: "400", color: "white", children: "Select Your Package" }), (tablesForArea === null || tablesForArea === void 0 ? void 0 : tablesForArea.every((table) => table.availableTables === 0)) && (_jsx(Text, { fontSize: "12px", fontWeight: "500", color: "#FF6B6B", children: "All tables are sold out" }))] }), _jsx(Box, { ref: swiperContainerRef, "data-swiper-container": "true", children: _jsx(Swiper, { effect: "coverflow", centeredSlides: true, slidesPerView: 1.6, spaceBetween: 15, loop: false, grabCursor: true, initialSlide: 0, onSwiper: (swiper) => {
                                // Ensure first table is selected when Swiper initializes
                                if (tablesForArea.length > 0) {
                                    // Always select the first table if none is selected
                                    if (!selectedTable) {
                                        setSelectedTable(tablesForArea[0]);
                                    }
                                }
                            }, onSlideChange: (swiper) => {
                                const selected = tablesForArea[swiper.activeIndex];
                                if (selected) {
                                    setSelectedTable(selected);
                                }
                            }, modules: [EffectCoverflow], style: {
                                width: "100%",
                                padding: "5px 0 5px 0",
                                overflow: "visible",
                            }, coverflowEffect: {
                                rotate: 0,
                                stretch: 0,
                                depth: 150,
                                modifier: 2,
                                slideShadows: false,
                            }, breakpoints: {
                                320: { slidesPerView: 1.2, spaceBetween: -10 },
                                768: { slidesPerView: 1.4, spaceBetween: -15 },
                                1024: { slidesPerView: 1.6, spaceBetween: -20 },
                            }, children: tablesForArea === null || tablesForArea === void 0 ? void 0 : tablesForArea.map((table) => {
                                const isSelected = (selectedTable === null || selectedTable === void 0 ? void 0 : selectedTable.slotId)
                                    ? selectedTable.slotId === table.slotId
                                    : (selectedTable === null || selectedTable === void 0 ? void 0 : selectedTable.categoryId) === table.categoryId;
                                const isTableInsufficientLadies = !isNewTableFormat &&
                                    table.minLadiesRequired > 0 &&
                                    femaleCount < table.minLadiesRequired;
                                return (_jsx(SwiperSlide, { children: _jsxs(Box, { position: "relative", display: "flex", justifyContent: "center", alignItems: "center", cursor: isTableInsufficientLadies ? "not-allowed" : "pointer", transform: "scale(0.9)", transition: "all 0.3s ease", _active: {
                                            transform: isTableInsufficientLadies
                                                ? "scale(0.9)"
                                                : "scale(1)",
                                        }, pointerEvents: isTableInsufficientLadies ? "none" : "auto", children: [table.isUltimate && (_jsxs(_Fragment, { children: [_jsx(Image, { src: `${IMG_URL}/icons/sparkle-top-right.svg`, alt: "sparkle", boxSize: "60px", position: "absolute", top: "-23px", right: { base: "10px", md: "35px" }, filter: "drop-shadow(0 0 6px #CBB6FF)", pointerEvents: "none" }), _jsx(Image, { src: `${IMG_URL}/icons/sparkle-bottom-left.svg`, alt: "sparkle", boxSize: "45px", position: "absolute", bottom: "-15px", left: { base: "10px", md: "40px" }, filter: "drop-shadow(0 0 6px #CBB6FF)", pointerEvents: "none" })] })), table.isUltimate ? (_jsx(ExperienceCard, { table: table, isSelected: isSelected, iconUrl: `${IMG_URL}/background/diamond.svg`, calculatePrice: calculatePrice, femaleCount: femaleCount })) : (_jsx(SeatingOptionCard, { table: table, isSelected: isSelected, calculatePrice: calculatePrice, femaleCount: femaleCount }))] }) }, table.slotId + table.categoryId));
                            }) }) }), selectedTable && (_jsx(Box, { py: 2, opacity: isPerksCardLoading ? 0 : 1, transform: isPerksCardLoading ? "translateY(20px)" : "translateY(0)", animation: shouldAnimate ? `${fadeInSlideUp} 0.4s ease-out` : undefined, transition: isPerksCardLoading
                            ? "opacity 0.1s ease-out, transform 0.1s ease-out"
                            : "none", children: _jsx(PerksCard, { perksList: selectedTable.perks || [], badges: {
                                vip: (_e = selectedTable.labels) === null || _e === void 0 ? void 0 : _e.includes("vip"),
                                addonsCount: (_f = selectedTable.perks) === null || _f === void 0 ? void 0 : _f.filter((perk) => perk && perk !== "").length,
                                fastFilling: true,
                            }, isSmokingAllowed: selectedTable.smokingAllowed, isUltimate: selectedTable.isUltimate }) }, `perks-${selectedTable.slotId || selectedTable.categoryId}`))] }), selectedTable && (_jsxs(Flex, { justify: "space-between", align: "center", bg: "#1A1A1A", py: 5, px: 3, borderRadius: "none", position: "fixed", bottom: "0", left: "0", w: "100%", zIndex: "1000", boxShadow: "0 -2px 10px rgba(0,0,0,0.5)", children: [_jsxs(Box, { children: [_jsxs(Text, { fontSize: "14px", fontWeight: 400, color: "white", children: ["Total :", " ", _jsxs(Text, { as: "span", fontSize: "14px", fontWeight: 400, color: "white", children: ["\u20B9", calculatePrice((_g = selectedTable === null || selectedTable === void 0 ? void 0 : selectedTable.coverPrice) !== null && _g !== void 0 ? _g : 0, (_h = selectedTable === null || selectedTable === void 0 ? void 0 : selectedTable.entryPrice) !== null && _h !== void 0 ? _h : 0, (_j = selectedTable === null || selectedTable === void 0 ? void 0 : selectedTable.discountValue) !== null && _j !== void 0 ? _j : 0, (_k = selectedTable === null || selectedTable === void 0 ? void 0 : selectedTable.discountType) !== null && _k !== void 0 ? _k : "")] })] }), isInsufficientLadies ? (_jsx(Text, { fontSize: "12px", fontWeight: 400, color: "#FF6B6B", children: "Add Required Ladies" })) : (_jsx(Text, { fontSize: "12px", fontWeight: 400, color: "#989898", children: (selectedTable === null || selectedTable === void 0 ? void 0 : selectedTable.isUltimate)
                                    ? "Ultimate Experience"
                                    : "Standard Table" }))] }), _jsx(Button, { bg: "#8155FE", color: "white", fontSize: "sm", fontWeight: "500", _hover: {
                            bg: selectedTable.availableTables === 0 || isInsufficientLadies
                                ? "#8155FE"
                                : "#6D39FF",
                        }, borderRadius: "md", px: 5, isDisabled: selectedTable.availableTables === 0 || isInsufficientLadies, opacity: selectedTable.availableTables === 0 || isInsufficientLadies
                            ? 0.5
                            : 1, cursor: selectedTable.availableTables === 0 || isInsufficientLadies
                            ? "not-allowed"
                            : "pointer", onClick: () => {
                            if (onProceed &&
                                selectedTable &&
                                !isInsufficientLadies &&
                                selectedTable.availableTables > 0) {
                                onProceed({ ...selectedTable, isNewTableFormat });
                            }
                        }, children: "Proceed To Review \u2192" })] }))] }));
}
